<template>
  <div class="sleep-record">
    <div class="record-header">
      <div class="title-wrapper">
        <div class="title-bar" />
        <h3 class="title">睡眠记录</h3>
      </div>
      <div class="time-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          :class="['tab-btn', { active: activeTab === tab.value }]"
          @click="handleTabChange(tab.value)"
        >
          {{ tab.label }}
        </button>
      </div>
    </div>

    <div class="record-content">
      <!-- 左侧环形图 -->
      <div class="left-chart">
        <div ref="pieChart" class="pie-chart" />
        <div class="chart-legend">
          <div v-for="(item, index) in displayPieData" :key="index" class="legend-item">
            <span class="legend-dot" :style="{ background: item.itemStyle.color }" />
            <span class="legend-text">{{ item.name }}</span>
            <span class="legend-value">{{ item.value }}天</span>
          </div>
        </div>
      </div>

      <!-- 右侧使用 HealthChart 组件 -->
      <div class="right-chart">
        <HealthChart
          ref="healthChart"
          title=""
          chart-type="line"
          :view-type="activeTab"
          :day-data="dayData"
          :primary-color="primaryColor"
          :secondary-color="secondaryColor"
          :bar-color="barColor"
          :icon-bg-color="iconBgColor"
          :chart-data="chartData"
          :x-axis-data="xAxisData"
          :y-axis-max="12"
          :area-opacity="0.25"
          :show-charts="showChart"
          chart-height="220px"
          :show-label="true"
          class="sleep-health-chart"
          @tab-change="onHealthChartTabChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import HealthChart from './HealthChart.vue'
import * as echarts from 'echarts'

export default {
  name: 'SleepRecord',
  components: {
    HealthChart
  },
  props: {
    sleepData: {
      type: Array,
      default: () => []
    },
    activeTab: {
      type: String,
      default: '3'
    },
    userCode: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      tabs: [
        { label: '日', value: '1' },
        { label: '周', value: '2' },
        { label: '月', value: '3' }
      ],
      pieChart: null,
      // 颜色配置（参考 healthPad 睡眠配色）
      primaryColor: '#9B7ED9',
      secondaryColor: '#D4C5F0',
      barColor: '#9B7ED9',
      iconBgColor: '#F3EFFF',
      // HealthChart 数据
      chartData: [],
      xAxisData: [],
      dayData: null,
      showChart: true,
      // 饼图数据（根据 typeTab 统计）
      pieData: []
    }
  },
  computed: {
    displayPieData() {
      return this.pieData.length > 0 ? this.pieData : [
        { value: 0, name: '正常', itemStyle: { color: '#52c41a' }},
        { value: 0, name: '超时', itemStyle: { color: '#faad14' }},
        { value: 0, name: '不足', itemStyle: { color: '#f5222d' }}
      ]
    }
  },
  watch: {
    sleepData: {
      deep: true,
      handler(newData) {
        if (newData && newData.length > 0) {
          this.processData(newData)
        }
      }
    },
    activeTab(newVal) {
      this.processData(this.sleepData)
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initPieChart()
      if (this.sleepData && this.sleepData.length > 0) {
        this.processData(this.sleepData)
      }
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.pieChart) {
      this.pieChart.dispose()
    }
  },
  methods: {
    // 处理数据，统计 typeTab 生成饼图数据
    processData(records) {
      if (!Array.isArray(records)) return

      // 统计 typeTab：相同值+1，不同值创建新对象
      const typeTabMap = {}
      records.forEach(item => {
        const type = item.typeTab || '未知'
        if (!typeTabMap[type]) {
          typeTabMap[type] = 0
        }
        typeTabMap[type]++
      })

      // 转换为饼图数据格式
      const colorMap = {
        '正常': '#52c41a',
        '充足': '#52c41a',
        '超时': '#faad14',
        '过多': '#faad14',
        '不足': '#f5222d',
        '过少': '#f5222d',
        '未达标': '#f5222d',
        '不达标': '#f5222d'
      }

      this.pieData = Object.keys(typeTabMap).map(type => ({
        value: typeTabMap[type],
        name: type,
        itemStyle: {
          color: colorMap[type] || '#999999'
        }
      }))

      // 更新饼图
      this.updatePieChart()

      // 准备 HealthChart 数据
      this.prepareChartData(records)
    },

    prepareChartData(records) {
      const type = this.activeTab

      if (type === '1') {
        // 日视图：显示最近7天趋势
        this.showChart = false
        const dates = []
        const values = []

        for (let i = 6; i >= 0; i--) {
          const d = new Date()
          d.setDate(d.getDate() - i)
          const dateStr = this.formatDate(d)
          dates.push(`${d.getMonth() + 1}.${d.getDate()}`)

          const record = records.find(r => r.recordDate === dateStr)
          values.push(record ? Number(record.sleepDuration || 0) : 0)
        }

        this.chartData = values
        this.xAxisData = dates

        // 日视图详细数据
        const today = this.formatDate(new Date())
        const todayRecord = records.find(r => r.recordDate === today)

        if (todayRecord) {
          const duration = Number(todayRecord.sleepDuration || 0)
          const hours = Math.floor(duration)
          const minutes = Math.round((duration - hours) * 60)

          this.dayData = {
            count: 1,
            summary: [
              { label: '睡眠时长', value: `${hours}小时${minutes > 0 ? minutes + '分' : ''}` },
              { label: '起床时间', value: todayRecord.recordEndTime || '-' }
            ],
            records: [{
              time: todayRecord.recordEndTime || '-',
              title: '睡眠记录',
              subtitle: `入睡时间: ${todayRecord.recordStartTime || '-'}`,
              tags: duration >= 7 ? [{ label: '充足', color: '#52C41A' }]
                : duration >= 5 ? [{ label: '正常', color: '#4A90E2' }]
                  : [{ label: '不足', color: '#FAAD14' }],
              value: duration,
              unit: '小时'
            }]
          }
        } else {
          this.dayData = {
            count: 0,
            summary: [{ label: '睡眠时长', value: '未记录' }],
            records: []
          }
        }
      } else {
        // 周/月视图
        this.showChart = true
        const fullDates = this.generateDateList(type)
        const dates = fullDates.map(date => {
          const parts = date.split('-')
          return `${parts[1]}.${parts[2]}`
        })

        const values = fullDates.map(dateStr => {
          const record = records.find(r => r.recordDate === dateStr)
          return record ? Number(record.sleepDuration || 0) : 0
        })

        this.chartData = values
        this.xAxisData = dates
        this.dayData = null
      }
    },

    handleTabChange(value) {
      this.$emit('tab-change', value)
    },

    onHealthChartTabChange(tab) {
      // 同步 HealthChart 的 tab 切换（如果点击的是 HealthChart 内部的 tab）
      const map = { '日': '1', '周': '2', '月': '3' }
      if (map[tab] && map[tab] !== this.activeTab) {
        this.handleTabChange(map[tab])
      }
    },

    updatePieChart() {
      if (this.pieChart) {
        this.pieChart.setOption({
          series: [{
            data: this.displayPieData
          }]
        })
      }
    },

    initPieChart() {
      const chartDom = this.$refs.pieChart
      if (!chartDom) return

      this.pieChart = echarts.init(chartDom)
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c}天 ({d}%)'
        },
        series: [
          {
            name: '睡眠状态',
            type: 'pie',
            radius: ['55%', '75%'],
            center: ['50%', '45%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 6,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 14,
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: this.displayPieData
          }
        ]
      }
      this.pieChart.setOption(option)
    },

    handleResize() {
      if (this.pieChart) {
        this.pieChart.resize()
      }
    },

    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    generateDateList(type) {
      const baseDate = new Date()
      let fullDates = []

      if (type === '2') {
        fullDates = this.getWeekDates(baseDate)
      } else if (type === '3') {
        fullDates = this.getMonthDates(baseDate.getFullYear(), baseDate.getMonth())
      } else {
        fullDates = [this.formatDate(baseDate)]
      }

      return fullDates
    },

    getWeekDates(baseDate) {
      const dates = []
      const day = baseDate.getDay()
      const monday = new Date(baseDate)
      monday.setDate(baseDate.getDate() - (day === 0 ? 6 : day - 1))

      for (let i = 0; i < 7; i++) {
        const date = new Date(monday)
        date.setDate(monday.getDate() + i)
        dates.push(this.formatDate(date))
      }
      return dates
    },

    getMonthDates(year, month) {
      const dates = []
      const lastDay = new Date(year, month + 1, 0).getDate()
      for (let d = 1; d <= lastDay; d++) {
        const date = new Date(year, month, d)
        dates.push(this.formatDate(date))
      }
      return dates
    }
  }
}
</script>

<style scoped>
.sleep-record {
  background: #fff;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.title-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-bar {
  width: 4px;
  height: 18px;
  background: #9B7ED9;
  border-radius: 2px;
}

.title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.time-tabs {
  display: flex;
  gap: 8px;
}

.tab-btn {
  padding: 4px 12px;
  border: 1px solid #e0e0e0;
  background: #fff;
  border-radius: 4px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s;
}

.tab-btn:hover {
  border-color: #4a7dff;
  color: #4a7dff;
}

.tab-btn.active {
  background: #4a7dff;
  border-color: #4a7dff;
  color: #fff;
}

.record-content {
  display: flex;
  gap: 20px;
}

.left-chart {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 160px;
  width: 180px;
  flex-shrink: 0;
}

.pie-chart {
  width: 160px;
  height: 140px;
}

.chart-legend {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
  width: 100%;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-text {
  color: #666;
  min-width: 36px;
}

.legend-value {
  color: #333;
  font-weight: 500;
  margin-left: auto;
}

.right-chart {
  flex: 1;
  min-height: 200px;
}

/* 隐藏 HealthChart 的 header，使用 SleepRecord 自己的 header */
.sleep-health-chart >>> .chart-header {
  display: none;
}

.sleep-health-chart >>> .health-chart-container {
  padding: 0;
  box-shadow: none;
}

.sleep-health-chart >>> .day-view-calendar {
  margin-bottom: 0;
}

@media screen and (max-width: 768px) {
  .record-content {
    flex-direction: column;
  }

  .left-chart {
    width: 100%;
    min-width: auto;
  }
}
</style>
